import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { DEFAULT_MAP_CENTER } from '../lib/constants';

interface UseMapCampsOptions {
  searchQuery?: string;
  selectedCategories: string[];
  radius: number | null;
  mapCenter?: [number, number] | null;
  userLocation?: { lat: number; lng: number } | { coords: [number, number] } | null;
  enabled?: boolean;
  queryKeyPrefix?: string;
  showAllCamps?: boolean; // When true, fetch all camps without location filtering
}

/**
 * Custom hook to fetch all camps for the map (without pagination)
 * This ensures the map shows all matching camps regardless of pagination
 * 
 * @param options - Configuration options for fetching map camps
 * @returns Query result with all camps for the map
 */
export function useMapCamps({
  searchQuery = '',
  selectedCategories = [],
  radius,
  mapCenter,
  userLocation,
  enabled = true,
  queryKeyPrefix = 'camps-for-map',
  showAllCamps = false
}: UseMapCampsOptions) {
  // Determine if we should use location-based filtering
  const getLocation = () => {
    if (!userLocation) return null;
    
    // Handle different userLocation shapes
    if ('lat' in userLocation && 'lng' in userLocation) {
      return { lat: userLocation.lat, lng: userLocation.lng };
    }
    if ('coords' in userLocation) {
      return { lat: userLocation.coords[0], lng: userLocation.coords[1] };
    }
    return null;
  };

  const location = getLocation();
  const shouldUseLocation = Boolean(
    !showAllCamps && location && radius && mapCenter && 
    mapCenter[0] !== DEFAULT_MAP_CENTER.lat && 
    mapCenter[1] !== DEFAULT_MAP_CENTER.lng
  );

  return useQuery(
    [queryKeyPrefix, searchQuery, selectedCategories, radius, mapCenter, location, showAllCamps],
    async () => {
      // If showing all camps (USA view), fetch without location filtering
      if (showAllCamps) {
        console.log(`🔍 [${queryKeyPrefix}] Fetching all camps for map (USA view)...`);
        
        try {
          const results = await api.camps.fetchFilteredCamps({
            search: searchQuery || '',
            categories: selectedCategories,
            radius: null,
            center: null,
            page: 1,
            pageSize: 500, // Large limit to get all camps for map
            sortBy: 'name',
          });
          
          const validCamps = results
            .filter((c: any) => c && c.id && c.name && c.latitude && c.longitude)
            .map((c: any) => ({
              ...c,
              latitude: c.latitude || 0,
              longitude: c.longitude || 0,
              distance_miles: null // No distance for USA view
            }));
          
          console.log(`🔍 [${queryKeyPrefix}] Fetched camps for map (USA view):`, validCamps.length);
          return validCamps;
        } catch (error) {
          console.error(`🔍 [${queryKeyPrefix}] Error fetching camps for map (USA view):`, error);
          return [];
        }
      }
      
      // Only fetch if we have a location (otherwise show no camps on map)
      if (!shouldUseLocation) {
        return [];
      }

      console.log(`🔍 [${queryKeyPrefix}] Fetching all camps for map (no pagination)...`);
      
      // Use proximitySearch for location-based queries (more efficient)
      try {
        const categoryIds = selectedCategories.length > 0 ? selectedCategories : undefined;
        const response = await api.camps.proximitySearch({
          latitude: mapCenter![0],
          longitude: mapCenter![1],
          radiusMiles: radius!,
          filters: categoryIds ? { categories: categoryIds } : undefined,
          searchQuery: searchQuery && searchQuery.trim() ? searchQuery.trim() : undefined,
          limit: 500, // Large limit to get all camps for map
          offset: 0,
        });
        
        const results = Array.isArray(response) ? response : (response?.results || []);
        const validCamps = results
          .filter((c: any) => c && c.id && c.name && c.latitude && c.longitude)
          .map((c: any) => ({
            ...c,
            latitude: c.latitude || 0,
            longitude: c.longitude || 0,
            distance_miles: typeof c.distance_miles === 'number' 
              ? c.distance_miles 
              : (c.distance_miles ? parseFloat(c.distance_miles) || 0 : 0)
          }));
        
        console.log(`🔍 [${queryKeyPrefix}] Fetched camps for map:`, validCamps.length);
        return validCamps;
      } catch (error) {
        console.error(`🔍 [${queryKeyPrefix}] Error fetching camps for map:`, error);
        return [];
      }
    },
    {
      enabled: Boolean(enabled && (shouldUseLocation || showAllCamps)),
      staleTime: 1000 * 60 * 2, // 2 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );
}

